import { IconButton } from 'src/components/atoms';
import { ErrorMessage } from 'src/components/atoms/FormErrorMessage';
import { calcSpacing } from 'src/components/foundations';
import { textStyleCss } from 'src/theme/atlas';
import styled from 'styled-components';
import { HEADER_SCROLL_OFFSET } from '../../types';

export const QuestionCardWrapper = styled.div`
  width: 100%;
  scroll-margin-top: ${HEADER_SCROLL_OFFSET}px;
`;

export const QuestionCardContainer = styled.div<{ $isCollapsed?: boolean }>`
  box-sizing: border-box;
  background-color: ${(props) =>
    props.theme.atlas.semantic.color.surface.default};
  border: 1px solid #dadada;
  border-radius: 12px;
  padding: 16px 8px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  min-width: 0;
  cursor: ${(props) => (props.$isCollapsed ? 'pointer' : 'default')};
  user-select: ${(props) => (props.$isCollapsed ? 'none' : 'auto')};
`;

export const QuestionCardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const StyledActionIconButton = styled(IconButton)`
  cursor: pointer !important;

  &:hover {
    cursor: pointer !important;
    background-color: transparent !important;
    opacity: 0.7;
  }

  &:active {
    opacity: 0.5;
  }

  button {
    cursor: pointer !important;
    width: 32px;
    height: 32px;
    padding: 4px;
    border-radius: 8px;
  }

  i {
    cursor: pointer !important;
    color: #6f7377 !important;
  }
`;

export const QuestionCardHeader = styled.div<{ $isCollapsed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  cursor: pointer;
  user-select: none;
`;

export const HeaderControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

export const HeaderIconButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 4px;
  border-radius: 8px;
  flex-shrink: 0;
  box-sizing: border-box;

  i {
    cursor: pointer;
  }
`;

export const ExpandCollapseButton = styled(HeaderIconButton)``;

export const DragHandle = styled(HeaderIconButton)`
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  i {
    pointer-events: none;
  }
`;

export const PreviewText = styled.p`
  font-family: ${(props) =>
    props.theme.atlas.typography.semantic.bodySmall.fontFamily};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.2px;
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  margin: 0;
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  white-space: pre-wrap;
`;

export const RequiredBadge = styled.span`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.caption)};
  font-weight: 600;
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
`;

export const QuestionCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 8px;
  cursor: default;
  user-select: auto;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  textarea {
    max-width: 100%;
    box-sizing: border-box;
  }

  input,
  select {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

export const QuestionFieldsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

export const QuestionTextFieldColumn = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
`;

export const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const StyledErrorMessage = styled(ErrorMessage)`
  margin-top: ${calcSpacing(0.5)}px;
  display: block;
`;

export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

export const FieldLabelRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
  min-height: 16px;
`;

export const FormLabel = styled.label`
  font-family: ${(props) =>
    props.theme.atlas.typography.semantic.caption.fontFamily};
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  height: 16px;
  letter-spacing: 0.3px;
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  margin: 0;
`;

export const RequiredMark = styled.span`
  font-weight: 400;
`;

export const FieldInputWrapper = styled.div`
  position: relative;
  width: 100%;

  input {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
`;

export const FieldMetaContainer = styled.div`
  margin-top: ${calcSpacing(0.5)}px;
`;

export const CharacterCounter = styled.span<{ isError?: boolean }>`
  font-family: ${(props) =>
    props.theme.atlas.typography.semantic.caption.fontFamily};
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-feature-settings:
    'tnum' on,
    'lnum' on;
  letter-spacing: 0.3px;
  flex-shrink: 0;
  color: ${(props) =>
    props.isError
      ? props.theme.atlas.semantic.color.status.error.text
      : props.theme.atlas.semantic.color.type.muted};
`;

export const DescriptionFieldWrapper = styled.div`
  width: 100%;

  textarea {
    resize: vertical;
    min-height: 84px;
  }
`;

export const DateTimeOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${calcSpacing(1.6)}px;
`;

export const DateTimeOptionRow = styled.div`
  display: flex;
  gap: ${calcSpacing(0.8)}px;
  align-items: flex-start;
`;

export const DateTimeOptionContent = styled.div`
  display: flex;
  flex-direction: column;
`;

export const DateTimeOptionLabel = styled.p`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.body)};
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  margin: 0;
`;

export const DateTimeOptionDescription = styled.p`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  color: ${(props) => props.theme.atlas.semantic.color.type.muted};
  margin: 0;
  line-height: 24px;
`;

export const NumericOptionsContainer = styled.div`
  display: flex;
  gap: ${calcSpacing(1)}px;
`;

export const NumericValueRangeContainer = styled.div`
  padding-left: ${calcSpacing(5.9)}px;
  display: flex;
  gap: ${calcSpacing(1.6)}px;

  @media (max-width: 1440px) {
    padding-left: ${calcSpacing(3.5)}px;
  }
`;

export const NumericValueField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${calcSpacing(0.8)}px;
`;

export const NumericValueLabel = styled.p`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.body)};
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  margin: 0;
`;

export const DatabaseFieldContainer = styled.div`
  display: flex;
  gap: ${calcSpacing(0.8)}px;
  align-items: flex-start;
`;

export const DatabaseFieldContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${calcSpacing(0.8)}px;
`;

export const DatabaseFieldIconWrapper = styled.div`
  margin-top: ${calcSpacing(0.5)}px;
`;

export const DatabaseFieldToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${calcSpacing(1)}px;
`;

export const DatabaseFieldInfoMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${calcSpacing(1)}px;
`;

export const DatabaseFieldInfoTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${calcSpacing(1)}px;
`;

export const DatabaseFieldInfoTitleText = styled.span`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  font-weight: 600;
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
`;

export const DatabaseFieldInfoDescription = styled.p`
  margin: 0;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.caption)};
  color: ${(props) => props.theme.atlas.semantic.color.type.muted};
`;
